import React, { useState, useEffect, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Card,
  CardBody,
  Row,
  Col,
  Badge,
  Button,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from "reactstrap";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { get, put } from "../../../helpers/api_helper";
import { TASKS_URL } from "../../../helpers/url_helper";
import cookieHelper from "../../../helpers/getCookieData";

const DoctorTasksWidget = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    overdue: 0,
    due_today: 0,
    upcoming: 0,
    top_tasks: []
  });

  const getUserRole = () => {
    try {
      const userCookie = cookieHelper.getCookie("_u");
      if (userCookie) {
        const parsed = JSON.parse(atob(userCookie));
        return parsed?.role || parsed?.user_role?.name || "";
      }
    } catch (e) {
      // fallback
    }
    return "";
  };

  const userRole = getUserRole();
  const isAdmin = userRole.toLowerCase().includes("admin") || userRole.toLowerCase() === "super_admin";
  const isReceptionist = userRole.toLowerCase().includes("reception") || userRole.toLowerCase().includes("front desk");

  const getWidgetTitle = () => {
    if (isAdmin) return t("Executive Clinic & Payment Reminders");
    if (isReceptionist) return t("Clinic Operations & Reminders");
    return t("Your Tasks & Reminders");
  };

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get(`${TASKS_URL}/dashboard-summary`);
      if (res && (res.success || res.body)) {
        setSummary(res.body || res.data || { overdue: 0, due_today: 0, upcoming: 0, top_tasks: [] });
      }
    } catch (err) {
      console.error("Error fetching tasks summary:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Complete Task
  const handleCompleteTask = async (taskId) => {
    try {
      await put(`${TASKS_URL}/${taskId}/status`, { status: "COMPLETED" });
      setSummary(prev => ({
        ...prev,
        top_tasks: prev.top_tasks.filter(task => task.id !== taskId),
        overdue: Math.max(0, prev.overdue - 1),
        due_today: Math.max(0, prev.due_today - 1)
      }));
      fetchSummary();
    } catch (err) {
      console.error("Error completing task:", err);
    }
  };

  // Snooze Task
  const handleSnoozeTask = async (taskId, hours) => {
    try {
      const snoozeTime = moment().add(hours, "hours").toDate();
      await put(`${TASKS_URL}/${taskId}/status`, {
        status: "SNOOZED",
        snoozed_until: snoozeTime
      });
      fetchSummary();
    } catch (err) {
      console.error("Error snoozing task:", err);
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "URGENT":
        return <Badge color="danger" className="font-size-10">{t("Urgent")}</Badge>;
      case "HIGH":
        return <Badge color="warning" className="font-size-10">{t("High")}</Badge>;
      default:
        return <Badge color="info" className="font-size-10">{t("Normal")}</Badge>;
    }
  };

  return (
    <Card className="shadow-sm border-0 mb-4">
      <CardBody>
        {/* Widget Header & Metrics */}
        <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
          <div className="d-flex align-items-center">
            <div className="avatar-xs me-2">
              <span className="avatar-title rounded bg-soft-primary text-primary font-size-16">
                <i className="bx bx-check-shield" />
              </span>
            </div>
            <div>
              <h5 className="card-title mb-0 font-size-15 fw-bold">{getWidgetTitle()}</h5>
              <div className="text-muted font-size-11">{t("Prioritized clinical action items")}</div>
            </div>
          </div>

          <Link
            to="/tasks-notifications"
            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center font-size-12 px-2 py-1"
          >
            {t("View All")} <i className="bx bx-chevron-right ms-1" />
          </Link>
        </div>

        {/* Counter Summary Pills */}
        <Row className="g-2 mb-3">
          <Col xs="4">
            <div className="p-2 rounded bg-soft-danger border border-danger border-opacity-25 text-center">
              <div className="font-size-11 text-danger fw-semibold d-flex align-items-center justify-content-center">
                <span className="badge bg-danger rounded-circle p-1 me-1" style={{ width: "6px", height: "6px" }} />
                {t("Overdue")}
              </div>
              <h4 className="my-1 text-danger fw-bold">{summary.overdue}</h4>
            </div>
          </Col>
          <Col xs="4">
            <div className="p-2 rounded bg-soft-warning border border-warning border-opacity-25 text-center">
              <div className="font-size-11 text-warning fw-semibold d-flex align-items-center justify-content-center">
                <span className="badge bg-warning rounded-circle p-1 me-1" style={{ width: "6px", height: "6px" }} />
                {t("Due Today")}
              </div>
              <h4 className="my-1 text-warning fw-bold">{summary.due_today}</h4>
            </div>
          </Col>
          <Col xs="4">
            <div className="p-2 rounded bg-soft-primary border border-primary border-opacity-25 text-center">
              <div className="font-size-11 text-primary fw-semibold d-flex align-items-center justify-content-center">
                <span className="badge bg-primary rounded-circle p-1 me-1" style={{ width: "6px", height: "6px" }} />
                {t("Upcoming")}
              </div>
              <h4 className="my-1 text-primary fw-bold">{summary.upcoming}</h4>
            </div>
          </Col>
        </Row>

        {/* Tasks List */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" size="sm" />
            <div className="text-muted font-size-12 mt-1">{t("Loading tasks...")}</div>
          </div>
        ) : summary.top_tasks.length === 0 ? (
          <div className="text-center py-4 bg-light rounded text-muted">
            <i className="bx bx-check-double font-size-24 text-success mb-1 d-block" />
            <div className="font-size-13 fw-medium">{t("All caught up!")}</div>
            <div className="font-size-11">{t("No pending or overdue tasks for today.")}</div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {summary.top_tasks.map((task) => {
              const isOverdue = task.due_at && moment(task.due_at).isBefore(moment(), "day");
              const isToday = task.due_at && moment(task.due_at).isSame(moment(), "day");

              return (
                <div
                  key={task.id}
                  className="p-2 border rounded d-flex align-items-center justify-content-between bg-white hover-shadow transition-all"
                >
                  <div className="d-flex align-items-center overflow-hidden me-2">
                    <button
                      type="button"
                      onClick={() => handleCompleteTask(task.id)}
                      className="btn btn-sm btn-outline-success p-1 rounded-circle me-2 flex-shrink-0"
                      title={t("Mark Complete")}
                      style={{ width: "26px", height: "26px", lineHeight: "1" }}
                    >
                      <i className="bx bx-check font-size-14" />
                    </button>

                    <div className="overflow-hidden">
                      <div className="d-flex align-items-center gap-1 mb-1">
                        <span className="fw-semibold font-size-13 text-dark text-truncate">
                          {task.title}
                        </span>
                        {getPriorityBadge(task.priority)}
                      </div>

                      <div className="d-flex align-items-center gap-2 font-size-11 text-muted">
                        {task.patient && (
                          <span className="badge bg-soft-info text-info font-size-10 px-1 py-0">
                            👤 {task.patient.first_name} {task.patient.last_name || ""}
                          </span>
                        )}
                        <span>
                          <i className="mdi mdi-clock-outline me-1" />
                          {isOverdue ? (
                            <span className="text-danger fw-semibold">
                              {t("Overdue")} ({moment(task.due_at).format("DD MMM, hh:mm A")})
                            </span>
                          ) : isToday ? (
                            <span className="text-warning fw-semibold">
                              {t("Today")} ({moment(task.due_at).format("hh:mm A")})
                            </span>
                          ) : (
                            moment(task.due_at).format("DD MMM, hh:mm A")
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions Dropdown */}
                  <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    <UncontrolledDropdown>
                      <DropdownToggle tag="button" className="btn btn-sm btn-light p-1 rounded">
                        <i className="bx bx-dots-vertical-rounded font-size-14" />
                      </DropdownToggle>
                      <DropdownMenu className="dropdown-menu-end font-size-12">
                        <DropdownItem onClick={() => handleCompleteTask(task.id)}>
                          <i className="bx bx-check text-success me-2" />
                          {t("Mark Done")}
                        </DropdownItem>
                        <DropdownItem onClick={() => handleSnoozeTask(task.id, 2)}>
                          <i className="bx bx-time text-warning me-2" />
                          {t("Snooze 2 Hours")}
                        </DropdownItem>
                        <DropdownItem onClick={() => handleSnoozeTask(task.id, 24)}>
                          <i className="bx bx-calendar text-primary me-2" />
                          {t("Snooze to Tomorrow")}
                        </DropdownItem>
                        {task.patient_id && (
                          <>
                            <DropdownItem divider />
                            <DropdownItem
                              onClick={() => history.push(`/patient/details/${task.patient_id}`)}
                            >
                              <i className="bx bx-user me-2" />
                              {t("Open Patient")}
                            </DropdownItem>
                          </>
                        )}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default DoctorTasksWidget;
